import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, Timestamp, doc, getDoc, query, where, orderBy } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  Box, Paper, Typography, TextField, Select, MenuItem,
  FormControl, InputLabel, Button, Alert, CircularProgress,
  InputAdornment, FormHelperText, Autocomplete
} from '@mui/material';
import './FormPermintaanBahan.css';

const FormPermintaanBahan = () => {
  const [bahanList, setBahanList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBahanId, setSelectedBahanId] = useState('');
  const [jumlahDiminta, setJumlahDiminta] = useState(1);
  const [satuan, setSatuan] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [dosenList, setDosenList] = useState([]);
  const [tujuanPenggunaan, setTujuanPenggunaan] = useState('Praktikum');
  const [selectedDosenPengampuId, setSelectedDosenPengampuId] = useState('');
  const [selectedDosenPembimbingId, setSelectedDosenPembimbingId] = useState('');
  
  // State for multiple items
  const [selectedItems, setSelectedItems] = useState([]);
  const [showItemList, setShowItemList] = useState(false);

  // Get current user info
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
           const userData = { uid: user.uid, email: user.email, ...userSnap.data() };
           if (userData.role === 'mahasiswa' || userData.role === 'dosen') {
               setCurrentUser(userData);
           } else {
               setError('Anda tidak diizinkan mengakses formulir ini.');
               setCurrentUser(null);
           }
        } else {
           setError('Data pengguna tidak ditemukan.');
           setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch available materials
  useEffect(() => {
    const fetchBahan = async () => {
      setLoading(true);
      setError('');
      try {
        const bahanRef = collection(db, 'bahan');
        const q = query(bahanRef, orderBy('nama'));
        const querySnapshot = await getDocs(q);

        const availableBahan = querySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(bahan => Number(bahan.stok) > 0);

        setBahanList(availableBahan);
      } catch (err) {
        console.error("Error fetching materials:", err);
        setError('Gagal memuat daftar bahan yang tersedia.');
      } finally {
        setLoading(false);
      }
    };
    fetchBahan();
  }, []);

  // Fetch Dosen list
  useEffect(() => {
    const fetchDosen = async () => {
      setError('');
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('role', '==', 'dosen'), orderBy('nama'));
        const querySnapshot = await getDocs(q);
        const dosenData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDosenList(dosenData);
      } catch (err) {
        console.error("Error fetching dosen list:", err);
        setError('Gagal memuat daftar dosen.');
      }
    };
    fetchDosen();
  }, []);

  // Function to add selected item to the list
  const addItemToList = () => {
    // Validate the current selection
    if (!selectedBahanId || jumlahDiminta <= 0) {
      setError('Harap pilih bahan dan masukkan jumlah yang valid.');
      return;
    }

    // Find selected item in bahan list
    const selectedBahan = bahanList.find(bahan => bahan.id === selectedBahanId);
    if (!selectedBahan) {
      setError('Bahan yang dipilih tidak valid atau tidak tersedia.');
      return;
    }

    // Validate quantity against stock
    if (Number(jumlahDiminta) > Number(selectedBahan.stok)) {
      setError(`Jumlah yang diminta (${jumlahDiminta}) melebihi stok yang tersedia (${selectedBahan.stok}).`);
      return;
    }

    // Add to selected items list
    const newItem = {
      id: selectedBahanId,
      nama: selectedBahan.nama,
      jumlah: Number(jumlahDiminta),
      satuan: satuan || selectedBahan.satuan,
      keterangan: selectedBahan.keterangan
    };

    // Check if item already exists in the list
    const existingItemIndex = selectedItems.findIndex(item => item.id === selectedBahanId);
    if (existingItemIndex >= 0) {
      // Update quantity if item already exists
      const updatedItems = [...selectedItems];
      updatedItems[existingItemIndex].jumlah += Number(jumlahDiminta);
      setSelectedItems(updatedItems);
    } else {
      // Add new item if it doesn't exist
      setSelectedItems([...selectedItems, newItem]);
    }

    // Reset selection
    setSelectedBahanId('');
    setJumlahDiminta(1);
    setSatuan('');
    setSearchQuery('');
    setShowItemList(true);
    setError('');
  };

  // Function to remove item from the list
  const removeItemFromList = (itemId) => {
    setSelectedItems(selectedItems.filter(item => item.id !== itemId));
    if (selectedItems.length <= 1) {
      setShowItemList(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent default form validation if items are already added
    if (selectedItems.length > 0) {
      // Stop browser validation for the search field
      const searchField = document.querySelector('input[aria-autocomplete="list"]');
      if (searchField) {
        searchField.setCustomValidity('');
      }
    }
    
    if (!currentUser) {
      setError('Anda harus login untuk mengajukan permintaan.');
      return;
    }
    
    if (selectedItems.length === 0) {
      setError('Harap tambahkan minimal satu bahan ke daftar permintaan dengan mengklik tombol "Tambahkan ke Daftar".');
      return;
    }

    if (currentUser.role === 'mahasiswa') {
      if (tujuanPenggunaan === 'Praktikum' && !selectedDosenPengampuId) {
        setError("Mahasiswa harus memilih Dosen Pengampu untuk Praktikum.");
        return;
      }
      if (tujuanPenggunaan === 'Penelitian' && !selectedDosenPembimbingId) {
        setError("Mahasiswa harus memilih Dosen Pembimbing untuk Penelitian.");
        return;
      }
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Generate a unique submission ID for this group of requests
      // Use timestamp with nanoseconds and random number to ensure uniqueness
      const submissionTimestamp = Timestamp.now();
      const randomSuffix = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
      const nanoseconds = submissionTimestamp.nanoseconds || 0;
      const submissionId = `${currentUser.uid}_${submissionTimestamp.seconds}_${nanoseconds}_${randomSuffix}`;
      
      // Submit all items with the same submission ID
      for (const item of selectedItems) {
        const requestData = {
          userId: currentUser.uid,
          namaUser: currentUser.nama || currentUser.email,
          userNim: currentUser.role === 'mahasiswa' ? (currentUser.nim || 'N/A') : 'N/A',
          userNip: currentUser.role === 'dosen' ? (currentUser.nip || 'N/A') : 'N/A',
          userProgramStudi: currentUser.role === 'mahasiswa' ? (currentUser.programStudi || 'N/A') : 'N/A',
          bahanId: item.id,
          namaBahan: item.nama,
          jumlahDiminta: item.jumlah,
          satuan: item.satuan,
          status: 'pending',
          tanggalPermintaan: submissionTimestamp,
          submissionId: submissionId, // Add submission ID to group requests
          tujuanPenggunaan: currentUser.role === 'mahasiswa' ? tujuanPenggunaan : 'Penelitian',
          dosenPengampuId: currentUser.role === 'mahasiswa' && tujuanPenggunaan === 'Praktikum' ? selectedDosenPengampuId : null,
          dosenPembimbingId: currentUser.role === 'mahasiswa' && tujuanPenggunaan === 'Penelitian' ? selectedDosenPembimbingId : null,
        };

        await addDoc(collection(db, 'permintaan_bahan'), requestData);
      }

      setSuccess(`${selectedItems.length} permintaan bahan berhasil diajukan.`);
      // Reset form
      setSelectedItems([]);
      setShowItemList(false);
      setSelectedBahanId('');
      setJumlahDiminta(1);
      setSatuan('');
      setTujuanPenggunaan('Praktikum');
      setSelectedDosenPengampuId('');
      setSelectedDosenPembimbingId('');

    } catch (err) {
      console.error("Error submitting material request:", err);
      setError(`Gagal mengajukan permintaan: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Get selected bahan details
  const selectedBahan = bahanList.find(b => b.id === selectedBahanId);

  // Get available units based on material type
  const getAvailableUnits = (bahan) => {
    if (!bahan) return [];
    if (bahan.jenis === 'cair') return ['ml'];
    if (bahan.jenis === 'serbuk') return ['mg', 'gram'];
    return [bahan.satuan || ''];
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>Form Permintaan Bahan</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <Autocomplete
            fullWidth
            options={bahanList}
            getOptionLabel={(option) => 
              typeof option === 'string' ? option : 
              `${option.nama} (Stok: ${option.stok} ${option.satuan})${option.keterangan ? ` - ${option.keterangan}` : ''}`
            }
            value={selectedBahan || null}
            onChange={(event, newValue) => {
              setSelectedBahanId(newValue?.id || '');
              setSatuan(newValue?.satuan || '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pilih Bahan"
                required={selectedItems.length === 0}
                sx={{ mb: 2 }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box component="li" key={key} {...otherProps}>
                <Box>
                  <Typography>
                    {option.nama}
                    <Typography component="span" color="text.secondary">
                      {` (Stok: ${option.stok} ${option.satuan})`}
                    </Typography>
                  </Typography>
                  {option.keterangan && (
                    <Typography variant="body2" color="text.secondary">
                      {option.keterangan}
                    </Typography>
                  )}
                </Box>
                </Box>
              );
            }}
          />

          {selectedBahan && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Bahan Terpilih:
              </Typography>
              <Typography>
                {selectedBahan.nama}
                <Typography component="span" color="text.secondary">
                  {` (Stok: ${selectedBahan.stok} ${selectedBahan.satuan})`}
                </Typography>
              </Typography>
              {selectedBahan.keterangan && (
                <Typography variant="body2" color="text.secondary">
                  {selectedBahan.keterangan}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Jumlah"
              type="number"
              value={jumlahDiminta}
              onChange={(e) => setJumlahDiminta(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: "1" }}
              required
              disabled={loading || !selectedBahanId}
              sx={{ flex: 1 }}
            />

            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Satuan</InputLabel>
              <Select
                value={satuan}
                onChange={(e) => setSatuan(e.target.value)}
                label="Satuan"
                disabled={!selectedBahan}
                required
              >
                {getAvailableUnits(selectedBahan).map(unit => (
                  <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button 
              variant="contained" 
              color="primary"
              onClick={addItemToList}
              disabled={!selectedBahanId || loading}
              sx={{ minWidth: 150 }}
            >
              Tambahkan ke Daftar
            </Button>
          </Box>

          {/* Selected Items List - Always visible */}
          <Box sx={{ mb: 3, mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Daftar Bahan yang Dipilih
            </Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                border: selectedItems.length === 0 ? '1px dashed #f44336' : '1px solid #e0e0e0',
                bgcolor: selectedItems.length === 0 ? 'rgba(244, 67, 54, 0.05)' : 'background.paper'
              }}
            >
              {selectedItems.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography color="error" sx={{ mb: 1 }}>
                    Belum ada bahan yang dipilih
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pilih bahan, tentukan jumlah, lalu klik "Tambahkan ke Daftar"
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {selectedItems.map((item, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        p: 1,
                        borderBottom: index < selectedItems.length - 1 ? '1px solid #eee' : 'none'
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle1">
                          {item.nama} ({item.jumlah} {item.satuan})
                        </Typography>
                        {item.keterangan && (
                          <Typography variant="body2" color="text.secondary">
                            {item.keterangan}
                          </Typography>
                        )}
                      </Box>
                      <Button 
                        size="small" 
                        color="error" 
                        onClick={() => removeItemFromList(item.id)}
                      >
                        Hapus
                      </Button>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Box>

          {currentUser?.role === 'mahasiswa' && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Tujuan Penggunaan</InputLabel>
                <Select
                  value={tujuanPenggunaan}
                  onChange={(e) => {
                    setTujuanPenggunaan(e.target.value);
                    // Reset dosen selection when changing purpose
                    setSelectedDosenPengampuId('');
                    setSelectedDosenPembimbingId('');
                  }}
                  required
                  disabled={loading}
                  label="Tujuan Penggunaan"
                >
                  <MenuItem value="Praktikum">Praktikum</MenuItem>
                  <MenuItem value="Penelitian">Penelitian</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>
                  {tujuanPenggunaan === 'Praktikum' ? 'Dosen Pengampu MK' : 'Dosen Pembimbing'}
                </InputLabel>
                <Select
                  value={tujuanPenggunaan === 'Praktikum' ? selectedDosenPengampuId : selectedDosenPembimbingId}
                  onChange={(e) => {
                    if (tujuanPenggunaan === 'Praktikum') {
                      setSelectedDosenPengampuId(e.target.value);
                      setSelectedDosenPembimbingId('');
                    } else {
                      setSelectedDosenPembimbingId(e.target.value);
                      setSelectedDosenPengampuId('');
                    }
                  }}
                  required
                  disabled={loading || dosenList.length === 0}
                  label={tujuanPenggunaan === 'Praktikum' ? 'Dosen Pengampu MK' : 'Dosen Pembimbing'}
                >
                  <MenuItem value="" disabled>
                    -- Pilih {tujuanPenggunaan === 'Praktikum' ? 'Dosen Pengampu' : 'Dosen Pembimbing'} --
                  </MenuItem>
                  {dosenList.map(dosen => (
                    <MenuItem key={dosen.id} value={dosen.id}>
                      {dosen.nama}
                    </MenuItem>
                  ))}
                </Select>
                {dosenList.length === 0 && !loading && (
                  <FormHelperText error>Tidak ada dosen tersedia/disetujui.</FormHelperText>
                )}
              </FormControl>
            </>
          )}

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !currentUser}
              sx={{ minWidth: 200 }}
            >
              {loading ? 'Mengajukan...' : 'Ajukan Permintaan'}
            </Button>
            {!currentUser && (
              <Typography color="error" sx={{ mt: 2 }}>
                Anda harus login untuk mengajukan permintaan.
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default FormPermintaanBahan;
